import { useCallback, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import jwtDecode from 'jwt-decode';

import { invalidateVisits } from 'store/visits/actions';
import { currentToken, currentUser, selectTokenRefreshing } from 'store/user/selectors';
import { apiUrlSelector } from 'store/config/selectors';
import { uiMetadata } from 'store/ui-metadata/selectors';
import { getMiniappSdkClient } from 'hooks/use-miniapp-sdk.hook';

/*
 * search-service pushes a Server-Sent-Events frame per activity change for the current user, so this
 * miniapp learns about a visit that was cancelled, checked in or edited SOMEWHERE ELSE — the activity
 * centre, another device — without the user refreshing.
 *
 * A frame carries the raw domain event, NOT the shape these pages render, so it is treated purely as a
 * SIGNAL TO RE-READ: it dispatches `invalidateVisits`, which the list re-reads its current window on.
 * It NEVER navigates or moves the user — a cancel pushed while they are filling in the form or looking
 * at a visit refreshes the underlying list quietly, it does not redirect them.
 *
 * Ported from the connection shape proven in miniapp-activity-center#56 / miniapp-service-booking#84 —
 * kept structurally aligned with that parent so a fix found in either transplants to the other.
 */

/*
 * `includeAllEventStatuses` is carried over from the sibling apps and is INERT for this one: search-
 * service's `isLandlordVisibleActivityItem` early-returns `true` for every non-`event` item, and a visit
 * is a `visitor_registration` doc, so its frames arrive at every status regardless of this flag (the flag
 * only gates helix `event` registrations). Kept for parity with the sibling streams; it costs nothing.
 */
const activityStreamUrl = (searchUrl: string, userUuid: string): string =>
  `${searchUrl}/search/v1/activity-events/events/${userUuid}?includeAllEventStatuses=true`;

// The stream interleaves `event: heartbeat` frames (empty data) every 25s so the load balancer doesn't
// drop the idle connection. They carry no change and must not trigger a refresh.
const HEARTBEAT_EVENT = 'heartbeat';

// Coalesce a burst of frames (a multi-session change lands as several) into a single refresh.
const REFRESH_DEBOUNCE_MS = 400;

// Reconnect backoff: a dropped stream retries, growing to a cap so a persistent outage doesn't hammer.
const RECONNECT_BASE_MS = 1_000;
const RECONNECT_MAX_MS = 30_000;

/*
 * How many token refreshes a single connection may spend on a failure it is CONFIDENT about, before
 * backing off like any other outage. Matches the shared query policy's `MAX_AUTH_RETRIES` — a host that
 * cannot mint a token must not become a refresh loop. A failure the client cannot corroborate gets a
 * tighter budget of its own; see `createActivityStreamConnection`.
 */
const MAX_AUTH_REFRESHES = 1;

/*
 * 401 AND 403. The earlier 401-only form reasoned that a 403 on this route is a permissions answer no
 * refresh can fix. That is right about what a 403 MEANS and wrong about what produces one: on this
 * route an expired token answers 403 too, so gating recovery on 401 made it dead code against the one
 * failure it exists for.
 *
 * search-service never answers 401 here. `AuthMiddleware` calls `next()` whatever the bearer turns out
 * to be, so an expired or unparseable token simply leaves no permission scopes attached; then
 * `LocalUserPermissionsGuard.canActivate` finds no scopes for `READ_USERS` and returns `false`, which
 * Nest raises as a ForbiddenException. Expiry and denial are the same status by construction, and the
 * response body does not separate them either.
 */
const AUTH_ERROR_STATUSES = new Set([401, 403]);

/*
 * 401 is treated as CONFIDENT regardless of what the token looks like. Unlike a 403 it carries no
 * permissions meaning at all — it says the credential was rejected — so there is nothing to weigh, and
 * gating it on `exp` would have regressed the behaviour this hook shipped with.
 */
const UNAUTHORIZED_STATUS = 401;

const isUnauthorized = (error: unknown): boolean =>
  typeof error === 'object' && error !== null && (error as WithStatusCode).statusCode === UNAUTHORIZED_STATUS;

interface WithStatusCode {
  statusCode?: unknown;
}

const isAuthError = (error: unknown): boolean =>
  typeof error === 'object' &&
  error !== null &&
  AUTH_ERROR_STATUSES.has((error as WithStatusCode).statusCode as number);

/*
 * How close to expiry counts as expired for this decision. The handshake has ALREADY come back 403, so
 * a token with seconds left on it was plausibly expired by the time the service read it, and the clocks
 * either side of a webview are not identical.
 */
const EXPIRY_SKEW_SECONDS = 30;

const millisecondsInSecond = 1000;

/*
 * The status alone cannot say whether a refresh would help, but the CLIENT HOLDS THE TOKEN, and reading
 * its `exp` splits the auth failures into two groups that deserve different budgets rather than a single
 * yes/no. The distinction is CONFIDENCE, not permission — see `recoverFromError`.
 *
 * Asking is not free here: `recoverAuth` sets the app-wide `isTokenRefreshing` flag, and
 * `checkTokenExpirationMiddleware` then queues EVERY other action behind a refresh the host may never
 * answer — ten seconds from redirecting the whole app to the critical-error page. So the budget for a
 * refresh nothing can justify is deliberately tighter than for one the token itself asks for.
 *
 * The `Bearer ` prefix is stripped because the store keeps the token prefixed (`updateToken` writes
 * `Bearer ${token}`) and a JWT decoder should be handed a JWT. Be aware that this line changes NOTHING
 * today and no test pins it: the pinned `jwt-decode` reads only the payload segment, so the prefix
 * corrupts the header segment harmlessly and the unstripped form decodes just as well. It is kept
 * because that is an accident of the library's internals, not a contract — `checkTokenExpirationMiddleware`
 * relies on the same accident — and a version that validates the header would break both silently.
 */
const tokenExpirySeconds = (token: string | null | undefined): number | undefined => {
  if (!token) {
    return undefined;
  }
  try {
    const { exp } = jwtDecode<{ exp?: number }>(token.replace(/^Bearer\s+/i, ''));

    return typeof exp === 'number' ? exp : undefined;
  } catch {
    return undefined;
  }
};

/*
 * Whether the token itself is visibly bad. `undefined` covers the three cases a caller cannot tell
 * apart — absent, malformed, carrying no `exp` — and none of them is evidence the token is good.
 */
const isTokenVisiblyBad = (token: string | null | undefined): boolean => {
  const expiry = tokenExpirySeconds(token);

  return expiry === undefined || expiry - EXPIRY_SKEW_SECONDS < Date.now() / millisecondsInSecond;
};

/**
 * Half the delay fixed, half random. A search-service deploy drops every client's stream at the same
 * instant, so an exact backoff has them all return together; the random half spreads that herd out.
 */
const withJitter = (delay: number): number => delay / 2 + Math.random() * (delay / 2);

const backoffDelay = (attempt: number): number =>
  withJitter(Math.min(RECONNECT_BASE_MS * 2 ** attempt, RECONNECT_MAX_MS));

const clearTimer = (timer: ReturnType<typeof setTimeout> | undefined): void => {
  if (timer) {
    clearTimeout(timer);
  }
};

/**
 * How long silence is allowed before the connection is presumed dead. A dropped TCP connection does not
 * always arrive as an error — a sleeping laptop, a network change, a load balancer reaping an idle
 * socket all leave a read that simply never resolves. The server heartbeats every 25s, so this is
 * THREE missed beats plus slack: too fast and a healthy connection is killed, which compounds because
 * backoff only resets on bytes.
 */
export const STREAM_IDLE_TIMEOUT_MS = 90_000;

/*
 * SSE permits CRLF, CR or LF as a line ending — independently — so a blank-line frame boundary can be
 * any two consecutive endings, INCLUDING mixed pairs like `\n\r\n`. Every ending is normalized to `\n`
 * as bytes arrive, so a boundary is always `\n\n`. A lone trailing `\r` is carried into the next read
 * rather than normalized now: it may be the front half of a CRLF split across two network chunks.
 */
const FRAME_LINE = /\r\n|\r|\n/;
const CARRIAGE_RETURN = '\r';
const normalizeLineEndings = (text: string): string => text.replace(/\r\n|\r/g, '\n');

const isHeartbeatFrame = (frame: string): boolean =>
  frame
    .split(FRAME_LINE)
    .some((line) => line.startsWith('event:') && line.slice('event:'.length).trim() === HEARTBEAT_EVENT);

/**
 * Pull every COMPLETE `\n\n`-terminated frame off the front of the buffer, calling `onFrame` for each
 * real (non-heartbeat) one, and return whatever partial frame is left for the next chunk.
 */
const drainFrames = (buffer: string, onFrame: () => void): string => {
  let rest = buffer;
  let boundary = rest.indexOf('\n\n');
  while (boundary !== -1) {
    const frame = rest.slice(0, boundary);
    if (frame && !isHeartbeatFrame(frame)) {
      onFrame();
    }
    rest = rest.slice(boundary + 2);
    boundary = rest.indexOf('\n\n');
  }
  return rest;
};

/**
 * A stateful decoder that turns the stream's byte chunks into whole SSE frames and calls `onFrame` for
 * each real one. Holds the cross-chunk state — a partial frame and a lone trailing `\r` that may be the
 * front half of a CRLF split across two chunks — so `readStream` stays a plain read loop.
 */
const createFrameParser = (onFrame: () => void): ((value: Uint8Array) => void) => {
  const decoder = new TextDecoder();
  let buffer = '';
  let heldCarriageReturn = false;

  return (value: Uint8Array): void => {
    const decoded: string = (heldCarriageReturn ? CARRIAGE_RETURN : '') + decoder.decode(value, { stream: true });
    heldCarriageReturn = decoded.endsWith(CARRIAGE_RETURN);
    buffer += normalizeLineEndings(heldCarriageReturn ? decoded.slice(0, -1) : decoded);
    buffer = drainFrames(buffer, onFrame);
  };
};

/**
 * Drain one SSE response body to completion, calling `onBytes` whenever any bytes arrive (proof the
 * pipe is alive, heartbeats included) and `onFrame` for each real change frame.
 */
const readStream = async (
  body: ReadableStream<Uint8Array>,
  onBytes: () => void,
  onFrame: () => void,
): Promise<void> => {
  const reader = body.getReader();
  const pushChunk = createFrameParser(onFrame);
  let streamDone = false;
  while (!streamDone) {
    // Sequential by nature — each SSE frame is read off the socket in order.
    // eslint-disable-next-line no-await-in-loop
    const { value, done } = await reader.read();
    streamDone = done;
    if (value) {
      onBytes();
      pushChunk(value);
    }
  }
};

/**
 * Open the per-user activity stream. Attaches `statusCode` to a non-ok handshake so the caller can tell
 * an expired token (recoverable by a refresh) from a plain network blip: raw `fetch` does not throw an
 * error carrying that field for free.
 */
const openStream = async (url: string, token: string | null | undefined, signal: AbortSignal): Promise<Response> => {
  const headers: Record<string, string> = { Accept: 'text/event-stream' };
  if (token) {
    headers.Authorization = token;
  }
  const response = await fetch(url, { method: 'GET', headers, signal });
  if (!response.ok || !response.body) {
    throw Object.assign(new Error(`activity stream responded ${response.status}`), { statusCode: response.status });
  }
  return response;
};

interface ActivityStreamConnectionDeps {
  url: string;
  getToken: () => string | null | undefined;
  onRefresh: () => void;
  // Returns whether a token refresh actually went out, so the caller only spends its bounded auth
  // allowance (and resets its backoff) when a refresh is genuinely in flight.
  recoverAuth: () => boolean;
}

interface ActivityStreamConnection {
  start: () => void;
  stop: () => void;
}

/**
 * The connection state machine, kept out of the hook (and structurally aligned with the parent's
 * `use-booking-activity-stream`) so the hook body stays thin wiring. Holds one SSE connection open,
 * reconnecting with jittered backoff, catching up after a drop, and recovering once from a 401.
 */
const createActivityStreamConnection = ({
  url,
  getToken,
  onRefresh,
  recoverAuth,
}: ActivityStreamConnectionDeps): ActivityStreamConnection => {
  let isTornDown = false;
  let currentAttempt: AbortController | undefined;
  let debounceTimer: ReturnType<typeof setTimeout> | undefined;
  let reconnectTimer: ReturnType<typeof setTimeout> | undefined;
  let reconnectAttempt = 0;
  /*
   * Whether any attempt has already failed — the whole question the catch-up asks. Tracked separately
   * from `reconnectAttempt` because that counter answers a DIFFERENT question (how long to wait) and the
   * auth recovery below rewinds it; gating the catch-up on the counter only worked while the
   * unconditional increment sat below every rewind, an invariant nothing stated.
   */
  let hasAttemptFailed = false;
  // The token the in-flight attempt actually sent. See where it is set in `connect`.
  let sentToken: string | null | undefined;
  /*
   * Two budgets, because the two kinds of auth failure deserve different ones.
   *
   * CONFIDENT — a 401, or a 403 on a token this client can see is expired, malformed or absent. The
   * token itself asks for the refresh, so this is the ordinary allowance: bounded per connection and
   * reset on bytes, which is what lets a session outliving several token lifetimes recover from each
   * expiry rather than only the first.
   */
  let confidentRefreshesUsed = 0;
  /*
   * SPECULATIVE — a 403 on a token that looks perfectly live. Two different things produce that and the
   * client cannot tell them apart:
   *
   *   a genuine permissions denial, which no refresh can fix
   *   a webview clock running behind the server's, so a token the server has already expired still
   *     reads as valid here — and one refresh DOES fix that, because the replacement is minted against
   *     the server's clock and reads as even more valid against the slow one
   *
   * One attempt, and — like the confident allowance — reset on BYTES. Bytes are what separates the two
   * cases after the fact: a denial is refused at every handshake and never delivers any, so its budget
   * stays spent and it cannot become a loop. A lagging clock that recovers DOES deliver, so when the
   * replacement token expires server-side in its turn it gets another correction rather than the stream
   * going quiet until the device clock catches up.
   *
   * Kept as a separate counter from the confident one rather than merged, so a stretch that changes
   * classification earns its second attempt: a 403 on a live token spends this, and if the token then
   * genuinely expires before any bytes arrive, that failure is confident and has its own allowance.
   */
  let hasSpentSpeculativeRefresh = false;
  // Forward-declared: `scheduleReconnect` schedules the next `connect`, and `connect`'s failure path
  // schedules a reconnect — a cycle no ordering of `const` arrows can satisfy.
  let connect: () => Promise<void>;

  const scheduleRefresh = (): void => {
    // A chunk whose `reader.read()` had already resolved can decode a frame in the same turn as teardown,
    // after `stop()` cleared `debounceTimer`. Gate here so that can't schedule a stray post-teardown
    // `dispatch(invalidateVisits())` 400ms later.
    if (isTornDown) {
      return;
    }
    clearTimer(debounceTimer);
    debounceTimer = setTimeout(onRefresh, REFRESH_DEBOUNCE_MS);
  };

  const scheduleReconnect = (): void => {
    const delay = backoffDelay(reconnectAttempt);
    reconnectAttempt += 1;
    reconnectTimer = setTimeout(() => {
      connect().catch(() => undefined);
    }, delay);
  };

  /*
   * A refused handshake, decided against the token THAT ATTEMPT SENT.
   *
   * The first branch is the one that is easy to miss: an app action can refresh the token while a
   * handshake is in flight, so the refusal can land after a newer credential has already reached the
   * store. Nothing is wrong with auth in that case and nothing needs asking for — the next attempt will
   * carry the new token — so this spends no budget and simply retries at once. Asking anyway would put a
   * second, redundant refresh in front of the app-wide flag.
   *
   * Otherwise the budgets apply; see the two counters. Either way the spend happens only when a refresh
   * ACTUALLY went out: `recoverAuth` returns false when one is already in flight or no SDK client exists
   * yet, and burning an allowance on that no-op would strand the stream on a dead token.
   */
  const recoverAuthIfItCanHelp = (error: unknown): void => {
    if (sentToken !== getToken()) {
      reconnectAttempt = 0;

      return;
    }

    const isConfident = isUnauthorized(error) || isTokenVisiblyBad(sentToken);
    const isWithinBudget = isConfident ? confidentRefreshesUsed < MAX_AUTH_REFRESHES : !hasSpentSpeculativeRefresh;
    if (!isWithinBudget || !recoverAuth()) {
      return;
    }

    if (isConfident) {
      confidentRefreshesUsed += 1;
    } else {
      hasSpentSpeculativeRefresh = true;
    }
    reconnectAttempt = 0;
  };

  const recoverFromError = (error: unknown): void => {
    // Whatever the reason, this connection did not survive — the next one is a replacement and owes the
    // caller a catch-up. Set before any recovery bookkeeping below touches the counters.
    hasAttemptFailed = true;

    /*
     * An unauthorized handshake is the one failure waiting longer cannot fix, and this stream is the
     * surface most likely to meet one: the only request that stays open across a token's whole lifetime.
     * This repo refreshes the token only when an action passes the check-token middleware, and this hook
     * dispatches nothing on an idle connection — so recovery has to happen here.
     */
    if (isAuthError(error)) {
      recoverAuthIfItCanHelp(error);
    }

    scheduleReconnect();
  };

  connect = async (): Promise<void> => {
    // One controller per attempt, so the watchdog can drop this connection and no other.
    const attempt = new AbortController();
    currentAttempt = attempt;

    // Idle watchdog kept LOCAL to this attempt: the timer aborts THIS connection, and its lifetime is
    // exactly this `connect` call (cleared in `finally`), so a reconnect can never clear a timer another
    // attempt owns.
    let idleTimer: ReturnType<typeof setTimeout> | undefined;
    const armIdleWatchdog = (): void => {
      clearTimer(idleTimer);
      idleTimer = setTimeout(() => attempt.abort(), STREAM_IDLE_TIMEOUT_MS);
    };

    // Armed BEFORE the awaits below: neither the token read nor `fetch` has a timeout of its own, so a
    // connect that never settles (a laptop that slept mid-handshake) would otherwise hang with no
    // watchdog running at all.
    armIdleWatchdog();

    try {
      /*
       * Held so the failure path can judge the token THIS attempt sent, not whatever is in the store by
       * the time it fails. An app action can refresh the token while a handshake is in flight, and the
       * old handshake's 403 then arrives after the fresh token has landed.
       */
      sentToken = getToken();
      const response = await openStream(url, sentToken, attempt.signal);

      // A successful RECONNECT (not the first connect) may have missed changes while it was down — catch
      // up once. The first connect is skipped: the list's own initial fetch already holds fresh data, and
      // re-invalidating it would double every cold start.
      if (hasAttemptFailed) {
        onRefresh();
      }
      armIdleWatchdog();

      await readStream(
        response.body as ReadableStream<Uint8Array>,
        () => {
          // Backoff and the auth allowance reset HERE, on bytes actually arriving — not on the handshake
          // returning. A gateway that accepts and immediately closes answers 200 every time; resetting
          // there would reconnect (and refetch) every second from every open client.
          reconnectAttempt = 0;
          confidentRefreshesUsed = 0;
          hasSpentSpeculativeRefresh = false;
          armIdleWatchdog();
        },
        scheduleRefresh,
      );
      // A closed stream is not the steady state — fall through to reconnect below.
      throw new Error('activity stream closed');
    } catch (error) {
      if (isTornDown) {
        return;
      }
      recoverFromError(error);
    } finally {
      clearTimer(idleTimer);
    }
  };

  return {
    start: (): void => {
      connect().catch(() => undefined);
    },
    stop: (): void => {
      isTornDown = true;
      // Aborting the live attempt rejects its awaited `fetch`/`reader.read()`, so `connect`'s `finally`
      // clears that attempt's idle watchdog a tick later — the watchdog is kept per-attempt (no cross-
      // attempt clear hazard), and this abort is what tears it down.
      currentAttempt?.abort();
      clearTimer(debounceTimer);
      clearTimer(reconnectTimer);
    },
  };
};

/**
 * Hold one SSE connection to search-service for the current user and dispatch `invalidateVisits`
 * whenever a real change lands, so a cancel/check-in/edit performed on another surface is reflected in
 * the visit list without a reload. Fetch-based rather than `EventSource` because the read needs the
 * app's `Authorization` header, which `EventSource` cannot set. Mount once, app-wide.
 */
export const useVisitActivityStream = (): void => {
  const user = useSelector(currentUser);
  const token = useSelector(currentToken);
  const apiUrl = useSelector(apiUrlSelector);
  const uiMeta = useSelector(uiMetadata);
  const isTokenRefreshing = useSelector(selectTokenRefreshing);
  const dispatch = useDispatch();

  const userUuid = user?.uuid;
  // The gateway fronts search-service in every deployed environment, so the app's own api origin is the
  // right host — no rewrite needed here (unlike the sibling apps that run search-service on a separate
  // local port with no gateway in between).
  const searchUrl = apiUrl ?? '';
  const appUuid = uiMeta?.app_uuid;

  const refresh = useCallback(() => dispatch(invalidateVisits()), [dispatch]);

  /*
   * Recover from an unauthorized handshake by asking the host for a fresh JWT. `useRefreshToken` receives
   * the answer and dispatches `updateToken`, which updates the token the next reconnect reads through the
   * ref below. Returns whether a request actually went out.
   *
   * IT DOES NOT SET `isTokenRefreshing`, and that is the load-bearing part. That flag is the check-token
   * middleware's gate: while it is true every action dispatched anywhere in the app is queued behind
   * `callActionAfterRefreshToken`, which waits for an `updateToken` and, failing that, times out into
   * `callActionAfterRefreshTokenTimeout` with an `UnauthorizedError`. So a background stream that sets it
   * can take down the user's foreground work over a connection they cannot see and did not ask for. It
   * only takes a host that does not answer — which is any surface with no native shell behind it, the
   * mocked e2e runs included.
   *
   * The REQUEST on its own is safe: if the host answers, `updateToken` lands and the next reconnect
   * carries it; if it does not, nothing else in the app is any worse off. The flag exists to coordinate
   * the middleware's own refreshes, where an action is already waiting on the result, and the middleware
   * still sets it when it sees an expired token. Nothing here needs that coordination.
   *
   * The flag is still READ, as a dedupe — no point stacking a request on a refresh already in flight —
   * and a null client (before `uiMetadata` lands) returns false rather than reporting a request that
   * never went out, so the caller does not spend a budget on a no-op.
   */
  const recoverAuth = useCallback((): boolean => {
    if (isTokenRefreshing) {
      return false;
    }
    const client = getMiniappSdkClient(appUuid);
    if (!client) {
      return false;
    }
    client.refreshToken();

    return true;
  }, [appUuid, isTokenRefreshing]);

  /*
   * Latest values held in refs so the connection effect depends only on identity (user/url), not on
   * references React hands us each render — otherwise it would reconnect on every re-render. Reading the
   * token through a ref also means a reconnect after a token refresh picks up the new value.
   */
  const refreshRef = useRef(refresh);
  useEffect(() => {
    refreshRef.current = refresh;
  }, [refresh]);

  const tokenRef = useRef(token);
  useEffect(() => {
    tokenRef.current = token;
  }, [token]);

  const recoverAuthRef = useRef(recoverAuth);
  useEffect(() => {
    recoverAuthRef.current = recoverAuth;
  }, [recoverAuth]);

  useEffect(() => {
    if (!userUuid || !searchUrl) {
      return undefined;
    }

    const connection = createActivityStreamConnection({
      url: activityStreamUrl(searchUrl, userUuid),
      getToken: () => tokenRef.current,
      onRefresh: () => refreshRef.current(),
      recoverAuth: () => recoverAuthRef.current(),
    });
    connection.start();

    return () => connection.stop();
  }, [userUuid, searchUrl]);
};
